import { Button, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { GetAntIcon } from "../utils/ant_icons";
import { TOperationData } from "../types/operation";
import { TWellData } from "../types/well";

interface SwimlaneFocusHelperProps {
	projectId: string;
	operation?: TOperationData & TWellData;
	className?: string;
}

// Utility function to generate focus URL
export const getSwimlaneFocusUrl = (
	baseUrl: string,
	swimlaneType: "project" | "operation",
	operationId?: string
) => {
	const params = new URLSearchParams();
	params.set("focus", swimlaneType);
	if (operationId) {
		params.set("id", operationId);
	}
	return `${baseUrl}?${params.toString()}`;
};

// Component for operation swimlane focus button
export const OperationFocusButton = ({
	projectId,
	operation,
	className = "",
}: SwimlaneFocusHelperProps) => {
	const navigate = useNavigate();

	const handleFocus = () => {
		const focusUrl = getSwimlaneFocusUrl(
			window.location.pathname,
			"operation",
			operation?._id
		);
		navigate(focusUrl);
	};

	if (!operation) return null;

	return (
		<Tooltip
			title={`Focus on ${operation.well_name} > ${operation.operation_name}`}
		>
			<Button
				type="text"
				size="small"
				icon={GetAntIcon("aim")}
				onClick={handleFocus}
				className={`focus-button ${className}`}
				style={{ marginLeft: "8px" }}
			/>
		</Tooltip>
	);
};

// Component for project swimlane focus button
export const ProjectFocusButton = ({
	projectId,
	className = "",
}: Omit<SwimlaneFocusHelperProps, "operation">) => {
	const navigate = useNavigate();

	const handleFocus = () => {
		const focusUrl = getSwimlaneFocusUrl(window.location.pathname, "project");
		navigate(focusUrl);
	};

	return (
		<Tooltip title="Focus on Project swimlane">
			<Button
				type="text"
				size="small"
				icon={GetAntIcon("aim")}
				onClick={handleFocus}
				className={`focus-button ${className}`}
				style={{ marginLeft: "8px" }}
			/>
		</Tooltip>
	);
};

// Hook to get current focus state from URL
export const useSwimlaneFocus = () => {
	const urlParams = new URLSearchParams(window.location.search);
	const focusType = urlParams.get("focus") as "project" | "operation" | null;
	const focusId = urlParams.get("id");

	return {
		focusType,
		focusId,
		isFocused: !!focusType,
		isProjectFocused: focusType === "project",
		isOperationFocused: focusType === "operation",
	};
};
